import React, { useEffect } from 'react';
import { Navigate, Routes, Route, useParams } from "react-router-dom";
import { Toaster } from 'sonner';

import "./App.css";
import ErrorBoundary from "./ErrorBoundary";
import Footer from "./components/Footer";
import { AttributionProvider } from "./components/AttributionContext";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";

import LeaguePage from "./pages/LeaguePage";
import LeagueSettingsPage from "./pages/LeagueSettingsPage";
import LeagueSimulationPage from "./pages/LeagueSimulationPage";
import UhOhTooEarlyPage from "./pages/UhOhTooEarlyPage";
import LeagueHistoryPage from "./pages/LeagueHistoryPage";
import LeagueCupPage from "./pages/LeagueCupPage";
import LeagueTradeHubPage from "./pages/LeagueTradeHubPage";
import InvalidLeaguePage from "./pages/InvalidLeaguePage";
import LeagueAccessDeniedPage from "./pages/LeagueAccessDeniedPage";
import NotFoundPage from "./pages/NotFoundPage";
import TestErrorPage from "./pages/TestErrorPage";

// Auth
import { AuthProvider } from "./components/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import PasswordResetPage from "./pages/PasswordResetPage";
import PasswordResetConfirmPage from "./pages/PasswordResetConfirmPage";
import DashboardPage from "./pages/DashboardPage";
import LandingPage from "./pages/LandingPage";
import SharedLeaguePage from "./pages/SharedLeaguePage";
import ContactPage from "./pages/ContactPage";
import MethodologyPage from "./pages/MethodologyPage";
import AdminKTCMappingsPage from "./pages/AdminKTCMappingsPage";
import AdminHistoricalStorePage from "./pages/AdminHistoricalStorePage";

// Playoff Pool imports
import { PlayoffPoolAuthProvider } from "./components/PlayoffPool/AuthContext";
import PlayoffPoolHome from "./pages/PlayoffPool/PlayoffPoolHome";
import LeagueSetup from "./pages/PlayoffPool/LeagueSetup";
import LeagueDetail from "./pages/PlayoffPool/LeagueDetail";
import DraftInterface from "./pages/PlayoffPool/DraftInterface";
import DraftedTeams from "./pages/PlayoffPool/DraftedTeams";
import JoinLeague from "./pages/PlayoffPool/JoinLeague";
import EditTeams from "./pages/PlayoffPool/EditTeams";

import AnalyticsBridge from "./components/AnalyticsBridge";
import Layout from "./components/Layout";
import favicon from "./assets/img/favicon.png";

const DemoAwareRoute = ({ children, ...protectedRouteProps }) => {
    const { provider } = useParams();
    if (provider === "demo") {
        return children;
    }
    return (
        <ProtectedRoute {...protectedRouteProps}>
            {children}
        </ProtectedRoute>
    );
};

const App = () => {
    useEffect(() => {
        // Dynamically set the favicon
        const link = document.createElement("link");
        link.rel = "icon";
        link.href = favicon;
        document.head.appendChild(link);
    }, []);

    // Handle Yahoo OAuth popup: Yahoo redirects to the root URL with ?code=&state=
    // The popup calls our backend to exchange the code, then notifies the opener and closes.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        if (!code || !state || !window.opener) return;

        fetch(`/api/auth/yahoo/process-callback/?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'ok') {
                    window.opener.postMessage({ type: 'yahoo_oauth_success' }, '*');
                } else {
                    window.opener.postMessage({ type: 'yahoo_oauth_error', reason: data.error || 'unknown' }, '*');
                }
            })
            .catch(() => {
                window.opener.postMessage({ type: 'yahoo_oauth_error', reason: 'token_exchange_failed' }, '*');
            })
            .finally(() => window.close());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthProvider>
            <AttributionProvider>
            <AnalyticsBridge />
            <ErrorBoundary>
            <Toaster
                position="top-right"
                toastOptions={{
                    style: {
                        fontFamily: 'Nunito, system-ui, sans-serif',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        borderRadius: '8px',
                    },
                    classNames: {
                        success: '!bg-green-light !text-green !border-border-card',
                        error: '!bg-coral-light !text-coral !border-coral',
                    },
                }}
            />
                <Layout>
                    <Routes>
                        {/* Public auth routes */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/password-reset" element={<PasswordResetPage />} />
                        <Route
                            path="/password-reset/confirm"
                            element={<PasswordResetConfirmPage />}
                        />

                        {/* Public landing page */}
                        <Route path="/" element={<LandingPage />} />
                        <Route
                            path="/demo"
                            element={
                                <Navigate
                                    to="/fantasy_stats/league/demo/2025/demo-2025-alpha/"
                                    replace
                                />
                            }
                        />

                        {/* Public share page — no auth required */}
                        <Route path="/share/:code" element={<SharedLeaguePage />} />

                        {/* Protected main-site routes */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <DashboardPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/fantasy_stats/league/:provider/:leagueYear/:leagueId/"
                            element={
                                <DemoAwareRoute>
                                    <LeaguePage />
                                </DemoAwareRoute>
                            }
                        />
                        <Route
                            path="/fantasy_stats/league/:provider/:leagueYear/:leagueId/settings/"
                            element={
                                <DemoAwareRoute>
                                    <LeagueSettingsPage />
                                </DemoAwareRoute>
                            }
                        />
                        <Route
                            path="/fantasy_stats/simulation/:provider/:leagueYear/:leagueId/"
                            element={
                                <DemoAwareRoute>
                                    <LeagueSimulationPage />
                                </DemoAwareRoute>
                            }
                        />
                        <Route
                            path="/fantasy_stats/uh-oh-too-early/:page/:provider/:leagueYear/:leagueId/"
                            element={
                                <ProtectedRoute>
                                    <UhOhTooEarlyPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/fantasy_stats/league-history/:provider/:leagueYear/:leagueId/"
                            element={
                                <DemoAwareRoute>
                                    <LeagueHistoryPage />
                                </DemoAwareRoute>
                            }
                        />
                        <Route
                            path="/fantasy_stats/league-cup/:provider/:leagueYear/:leagueId/"
                            element={
                                <DemoAwareRoute>
                                    <LeagueCupPage />
                                </DemoAwareRoute>
                            }
                        />
                        <Route
                            path="/fantasy_stats/trade-hub/:provider/:leagueYear/:leagueId/"
                            element={
                                <DemoAwareRoute>
                                    <LeagueTradeHubPage />
                                </DemoAwareRoute>
                            }
                        />
                        <Route
                            path="/fantasy_stats/invalid-league/"
                            element={
                                <ProtectedRoute>
                                    <InvalidLeaguePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/fantasy_stats/league-access-denied/"
                            element={
                                <ProtectedRoute>
                                    <LeagueAccessDeniedPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* Playoff Pool Routes (own auth) */}
                        <Route
                            path="/playoff-pool/*"
                            element={
                                <PlayoffPoolAuthProvider>
                                    <Routes>
                                        <Route
                                            index
                                            element={<PlayoffPoolHome />}
                                        />
                                        <Route
                                            path="join"
                                            element={<JoinLeague />}
                                        />
                                        <Route
                                            path="create-league"
                                            element={<LeagueSetup />}
                                        />
                                        <Route
                                            path="league/:leagueId"
                                            element={<LeagueDetail />}
                                        />
                                        <Route
                                            path="league/:leagueId/edit-teams"
                                            element={<EditTeams />}
                                        />
                                        <Route
                                            path="league/:leagueId/draft"
                                            element={<DraftInterface />}
                                        />
                                        <Route
                                            path="league/:leagueId/teams"
                                            element={<DraftedTeams />}
                                        />
                                    </Routes>
                                </PlayoffPoolAuthProvider>
                            }
                        />

                        <Route path="/contact/" element={<ContactPage />} />
                        <Route path="/methodology" element={<MethodologyPage />} />
                        <Route path="/privacy" element={<PrivacyPolicyPage />} />
                        <Route path="/test-error" element={<TestErrorPage />} />
                        <Route
                            path="/ktc-mappings"
                            element={
                                <ProtectedRoute requireKtcAdmin>
                                    <AdminKTCMappingsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin-historical-store"
                            element={
                                <ProtectedRoute requireKtcAdmin>
                                    <AdminHistoricalStorePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    <Footer />
                </Layout>
            </ErrorBoundary>
            </AttributionProvider>
        </AuthProvider>
    );
};

export default App;
